import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { ChevronRight, Loader2, Star } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import { hasLivePortalSession } from '@/stores/portalAuth.store';
import { usePortalMemberships, type PortalMembership } from '@/hooks/portal/usePortalMemberships';
import { PortalShell } from '@/components/portal/PortalShell';
import { PortalStatusPill, type PortalPillTone } from '@/components/portal/PortalStatusPill';
import { formatBilling, formatRenews } from '@/lib/portal/membershipDisplay';

export const Route = createFileRoute('/(portal)/portal/memberships/')({
  beforeLoad: () => {
    if (!hasLivePortalSession()) {
      throw redirect({ to: '/portal/login' });
    }
  },
  component: PortalMembershipsPage,
});

const STATUS: Record<string, { label: string; tone: PortalPillTone }> = {
  ACTIVE: { label: 'Active', tone: 'success' },
  PENDING: { label: 'Pending', tone: 'accent' },
  PAST_DUE: { label: 'Past due', tone: 'accent' },
  SUSPENDED: { label: 'Suspended', tone: 'accent' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  EXPIRED: { label: 'Expired', tone: 'neutral' },
};

function statusOf(status: string): { label: string; tone: PortalPillTone } {
  return STATUS[status] ?? { label: status.toLowerCase(), tone: 'neutral' };
}

function PortalMembershipsPage() {
  const memberships = usePortalMemberships();

  return (
    <PortalShell>
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Membership</h1>
        <p className="text-sm text-muted-foreground">
          Your plan, benefits and what&apos;s included.
        </p>
      </div>

      {memberships.isPending ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : memberships.isError ? (
        <Card>
          <CardContent className="space-y-3 py-6 text-sm text-muted-foreground">
            <p>We couldn&apos;t load your memberships. Please try again.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void memberships.refetch()}
              disabled={memberships.isFetching}
            >
              {memberships.isFetching ? <Loader2 className="size-4 animate-spin" /> : null}
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : memberships.data.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            You don&apos;t have any memberships yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {memberships.data.map((m) => (
            <MembershipCard key={m.id} membership={m} />
          ))}
        </div>
      )}
    </PortalShell>
  );
}

/** A compact list row — the plan at a glance; the full dashboard lives on detail. */
function MembershipCard({ membership }: { membership: PortalMembership }) {
  const status = statusOf(membership.status);
  return (
    <Link to="/portal/memberships/$id" params={{ id: membership.id }} className="block">
      <Card className="transition-colors hover:border-portal-accent/40 hover:bg-muted/40">
        <CardContent className="flex items-center gap-3 py-4">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-portal-accent-surface text-portal-accent">
            <Star className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate text-sm font-semibold">
                {membership.planName ?? `Membership ${membership.membershipNumber}`}
              </p>
              <PortalStatusPill label={status.label} tone={status.tone} />
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {formatBilling(membership)}
              {formatRenews(membership)}
            </p>
          </div>
          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
        </CardContent>
      </Card>
    </Link>
  );
}
